// utils/exportUtils.js or components/dashboard/ExportData.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; 
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const exportToPDF = (data, stages, totals, filterType, divisionName, orgDetails) => {
  const doc = new jsPDF("landscape");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // --- Add Organization Logo at Top Center ---
  let currentY = 5;
  
if (orgDetails?.organizationPicture) {
  try {
    const logoWidth = 30;
    const logoHeight = 30;
    const centerX = pageWidth / 2;

    // Draw logo
    doc.addImage(
      orgDetails.organizationPicture,
      "PNG",
      centerX - logoWidth / 2,
      currentY,
      logoWidth,
      logoHeight
    );

    // Add consistent space *below* logo
    currentY += logoHeight + 15; // 15 extra px for spacing
  } catch (error) {
    console.error("Error adding logo:", error);
    currentY += 15; // fallback spacing if logo fails
  }
} else {
  currentY += 15; // add some top spacing even without logo
}


  // --- Organization Name ---
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(orgDetails?.name || "CuSo Biz", pageWidth / 2, currentY, { align: "center" });
  currentY += 18;

  // --- Title ---
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Salesperson Performance Dashboard", pageWidth / 2, currentY, { align: "center" });
  currentY += 10;

  // --- Red line separator ---
  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(1);
  doc.line(10, currentY, pageWidth - 10, currentY);
  currentY += 10;

  // --- Left Side: Generation Info ---
  const leftMargin = 14;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const date = new Date().toLocaleDateString("en-IN", { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
  doc.text(`Generated on: ${date}`, leftMargin, currentY);
  doc.text(`Filter: ${filterType?.charAt(0).toUpperCase() + filterType?.slice(1) || "Fiscal"}`, leftMargin, currentY + 6);
  doc.text(`Division: ${divisionName || "All Divisions"}`, leftMargin, currentY + 12);

  // --- Right Side: Summary ---
  const rightX = pageWidth - 80;
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Summary", rightX, currentY);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Active Salespersons: ${data.length}`, rightX, currentY + 6);
  doc.text(`Total Deals: ${totals.deals}`, rightX, currentY + 12);
  
  // Format the total pipeline value
  const formattedAmount = totals.amount === 0 ? '0' : 
    ` ${totals.amount.toLocaleString('en-IN')}`;
  doc.text(`Total Pipeline Value: ${formattedAmount}`, rightX, currentY + 18);
  
  currentY += 30;

  // --- Top Performers Section ---
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Top Performers", pageWidth / 2, currentY, { align: "center" });
  currentY += 8;

  // Calculate top performers
  const topPerformers = [...data]
    .sort((a, b) => b.totalAmount - a.totalAmount)
    .slice(0, 3);

  if (topPerformers.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    topPerformers.forEach((performer, index) => {
      const rank = index + 1;
      // const rankText = rank === 1 ? "1st" : rank === 2 ? "2nd" : "3rd";
      const name = performer.userName || `User ${rank}`;
      const amount = performer.totalAmount || 0;
      const deals = performer.totalDeals || 0;
      
      const text = `${rank}. ${name} - ${deals} deals ( ${amount.toLocaleString('en-IN')})`;
      doc.text(text, pageWidth / 2, currentY, { align: "center" });
      currentY += 6;
    });
  } else {
    doc.setFontSize(9);
    doc.text("No data available", pageWidth / 2, currentY, { align: "center" });
    currentY += 6;
  }

  currentY += 5;

  // --- Table Section ---
  const tableHeaders = [
    ["Rank", "Salesperson", ...stages.map(stage => stage.stageId), "Total Deals", "Total Amount"]
  ];

  // Prepare table data
  const tableRows = data.map((person, index) => {
    const row = [
      (index + 1).toString(),
      person.userName || "Unknown"
    ];

    // Add stage data
    stages.forEach(stage => {
      const stageData = person.stages?.find(s => s.stageId === stage.stageId);
      if (stageData && stageData.totalDeals > 0) {
        row.push(stageData.totalDeals.toString());
      } else {
        row.push("-");
      }
    });

    // Add totals
    row.push(person.totalDeals?.toString() || "0");
    row.push(` ${(person.totalAmount || 0).toLocaleString('en-IN')}`);

    return row;
  });

  // Add TOTAL row
  const totalRow = ["", "TOTAL"];
  
  // Calculate stage totals
  stages.forEach(stage => {
    const stageTotal = data.reduce((sum, person) => {
      const stageData = person.stages?.find(s => s.stageId === stage.stageId);
      return sum + (stageData?.totalDeals || 0);
    }, 0);
    totalRow.push(stageTotal > 0 ? stageTotal.toString() : "-");
  });
  
  totalRow.push(totals.deals?.toString() || "0");
  totalRow.push(` ${(totals.amount || 0).toLocaleString('en-IN')}`);
  
  tableRows.push(totalRow);

  // --- Generate Table ---
  autoTable(doc, {
    head: tableHeaders,
    body: tableRows,
    startY: currentY,
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 3,
      valign: 'middle',
      halign: 'center',
      lineColor: [200, 200, 200],
      lineWidth: 0.25,
    },
    headStyles: {
      fillColor: [220, 38, 38], // Red background
      textColor: 255,
      fontStyle: "bold",
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 20, halign: 'center' }, // Rank
      1: { cellWidth: 50, halign: 'left' },   // Salesperson
      [tableHeaders[0].length - 2]: { cellWidth: 25, halign: 'center' }, // Total Deals
      [tableHeaders[0].length - 1]: { cellWidth: 35, halign: 'right' },  // Total Amount
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    didParseCell: function(data) {
      // Style the TOTAL row
      if (data.row.index === tableRows.length - 1) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [254, 226, 226]; // Light red
      }
      
      // Style rank column for top 3
      if (data.column.index === 0 && data.row.index < 3 && data.section === 'body') {
        data.cell.styles.textColor = [220, 38, 38]; // Red text
        data.cell.styles.fontStyle = "bold";
      }

      // Align amounts to the right
      if (data.column.dataKey === data.table.columns.length - 1) {
        data.cell.styles.halign = 'right';
      }
    },
  });

  // --- Footer ---
  // const finalY = doc.lastAutoTable.finalY || currentY + 50;
  
  // Add footer at the bottom of the page
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Page 1 of 1 | Generated by ${orgDetails?.name || "CuSo Biz"} CRM`,
    pageWidth / 2,
    pageHeight - 10,
    { align: "center" }
  );

  // --- Save PDF ---
  const fileName = `salesperson-performance-${filterType || 'report'}-${
    new Date().toISOString().split('T')[0]
  }.pdf`;
  doc.save(fileName);
};


export const exportToExcel = (data, stages, totals, filterType, divisionName) => {
  // Create a new workbook
  const wb = XLSX.utils.book_new();
  
  // Sheet 1: Summary
  const summaryData = [
    ['Salesperson Performance Dashboard'],
    [],
    ['Generated on:', new Date().toLocaleDateString()],
    ['Filter Type:', filterType.charAt(0).toUpperCase() + filterType.slice(1)],
    ['Division:', divisionName],
    [],
    ['Summary Statistics'],
    ['Active Salespersons:', data.length],
    ['Total Deals:', totals.deals],
    ['Total Pipeline Value:', `₹${totals.amount.toLocaleString('en-IN')}`],
    [],
    ['Top 3 Performers'],
  ];
  
  // Add top 3 performers from the same filtered data
  const topPerformers = [...data]
    .sort((a, b) => b.totalAmount - a.totalAmount)
    .slice(0, 3);
  
  topPerformers.forEach((performer, index) => {
    summaryData.push([
      `${index + 1}. ${performer.userName}`,
      `₹${performer.totalAmount.toLocaleString('en-IN')}`,
      `${performer.totalDeals} deals`
    ]);
  });
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
  
  // Sheet 2: Detailed Performance Data
  const detailHeaders = [
    'Rank',
    'Salesperson',
    ...stages.flatMap(stage => [`${stage.stageId} Deals`, `${stage.stageId} Amount`]),
    'Total Deals',
    'Total Amount'
  ];
  
  const detailRows = data.map((person, index) => {
    const row = [index + 1, person.userName];
    
    stages.forEach(stage => {
      const personStage = person.stages.find(s => s.stageId === stage.stageId);
      row.push(personStage?.totalDeals || 0);
      row.push(personStage?.totalAmount || 0);
    });
    
    row.push(person.totalDeals);
    row.push(person.totalAmount);
    
    return row;
  });
  
  // Add total row
  const totalRow = ['', 'TOTAL'];
  stages.forEach(stage => {
    const stageTotal = data.reduce((acc, person) => {
      const personStage = person.stages.find(s => s.stageId === stage.stageId);
      return {
        deals: acc.deals + (personStage?.totalDeals || 0),
        amount: acc.amount + (personStage?.totalAmount || 0)
      };
    }, { deals: 0, amount: 0 });
    totalRow.push(stageTotal.deals);
    totalRow.push(stageTotal.amount);
  });
  totalRow.push(totals.deals);
  totalRow.push(totals.amount);
  
  const detailData = [detailHeaders, ...detailRows, [], totalRow];
  const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
  
  // Apply column widths
  const colWidths = [
    { wch: 6 },  // Rank
    { wch: 25 }, // Salesperson
    ...stages.flatMap(() => [{ wch: 10 }, { wch: 15 }]), // Stage columns
    { wch: 12 }, // Total Deals
    { wch: 18 }, // Total Amount
  ];
  detailSheet['!cols'] = colWidths;
  
  XLSX.utils.book_append_sheet(wb, detailSheet, 'Performance Details');
  
  // Sheet 3: Stage Summary
  const stageHeaders = ['Stage ID', 'Stage Name', 'Total Deals', 'Total Amount', 'Average Deal Size'];
  const stageRows = stages.map(stage => {
    const stageTotal = data.reduce((acc, person) => {
      const personStage = person.stages.find(s => s.stageId === stage.stageId);
      return {
        deals: acc.deals + (personStage?.totalDeals || 0),
        amount: acc.amount + (personStage?.totalAmount || 0)
      };
    }, { deals: 0, amount: 0 });
    
    return [
      stage.stageId,
      stage.stageName,
      stageTotal.deals,
      stageTotal.amount,
      stageTotal.deals > 0 ? Math.round(stageTotal.amount / stageTotal.deals) : 0
    ];
  });
  
  const stageData = [stageHeaders, ...stageRows];
  const stageSheet = XLSX.utils.aoa_to_sheet(stageData);
  stageSheet['!cols'] = [
    { wch: 10 },
    { wch: 25 },
    { wch: 12 },
    { wch: 15 },
    { wch: 18 }
  ];
  XLSX.utils.book_append_sheet(wb, stageSheet, 'Stage Summary');
  
  // Generate and save the Excel file
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const fileName = `salesperson-performance-${filterType}-${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
};
